import { useCallback, useState } from 'react'
import { useMutation } from 'convex/react'
import { Share2, Check, Link2, ExternalLink, Play } from 'lucide-react'
import { api } from '../../../convex/_generated/api'
import type { Id } from '../../../convex/_generated/dataModel'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Switch } from '@/components/ui/switch'
import { track } from '@/lib/track'
import { toast } from '@/hooks/use-toast'

// Header "Share" menu: public conversation replay toggle + final video page link.
export function ShareProjectButton({
  workflowId,
  workflow,
}: {
  workflowId: Id<'workflows'> | null
  workflow: { shortcode?: string; finalVideoUrl?: string; conversationSharedAt?: number } | null | undefined
}) {
  const setConversationShared = useMutation(api.workflows.setConversationShared)
  const [copied, setCopied] = useState<'chat' | 'video' | null>(null)
  const [saving, setSaving] = useState(false)

  const shortcode = workflow?.shortcode
  const shared = !!workflow?.conversationSharedAt
  const chatUrl = shortcode ? `${window.location.origin}/share/chat/${shortcode}` : null
  const videoUrl = shortcode ? `${window.location.origin}/share/${shortcode}` : null

  const handleToggle = useCallback(
    async (enabled: boolean) => {
      if (!workflowId || saving) return
      setSaving(true)
      try {
        await setConversationShared({ id: workflowId, shared: enabled })
        track('share.conversation_toggled', { workflowId: workflowId as string, enabled })
      } catch {
        toast({ title: 'Could not update sharing', variant: 'destructive' })
      } finally {
        setSaving(false)
      }
    },
    [workflowId, saving, setConversationShared]
  )

  const copy = useCallback(async (url: string, kind: 'chat' | 'video') => {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(kind)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      toast({ title: 'Could not copy link', variant: 'destructive' })
    }
  }, [])

  if (!workflowId) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg text-[13px] font-medium border border-zinc-200/80 bg-white text-zinc-900 hover:bg-zinc-50 shadow-sm transition-colors"
        >
          <Share2 className="h-3.5 w-3.5" />
          Share
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[300px] p-3">
        {!shortcode ? (
          <div className="text-[13px] text-zinc-500 px-1 py-2">
            This project doesn't have a share link yet.
          </div>
        ) : (
          <div className="space-y-3">
            {/* Public conversation replay */}
            <div>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[13px] font-medium text-zinc-900">Share conversation</div>
                  <p className="mt-0.5 text-[12px] leading-snug text-zinc-500">
                    Anyone with the link can watch a replay of this project's full chat.
                  </p>
                </div>
                <Switch
                  checked={shared}
                  disabled={saving}
                  onCheckedChange={handleToggle}
                  className="mt-0.5 data-[state=checked]:bg-teal-600"
                />
              </div>
              {shared && chatUrl && (
                <div className="mt-2 flex items-center gap-1.5">
                  <div className="flex-1 min-w-0 px-2 py-1.5 rounded-md bg-zinc-50 border border-zinc-200/80 text-[12px] text-zinc-500 truncate font-mono">
                    {chatUrl.replace(/^https?:\/\//, '')}
                  </div>
                  <button
                    type="button"
                    title="Copy link"
                    onClick={() => copy(chatUrl, 'chat')}
                    className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors shrink-0"
                  >
                    {copied === 'chat' ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
                  </button>
                  <button
                    type="button"
                    title="Watch replay"
                    onClick={() => window.open(chatUrl, '_blank')}
                    className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors shrink-0"
                  >
                    <Play className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>

            {/* Final video page */}
            {workflow?.finalVideoUrl && videoUrl && (
              <div className="pt-3 border-t border-zinc-100">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium text-zinc-900">Video page</div>
                    <p className="mt-0.5 text-[12px] leading-snug text-zinc-500">
                      Public page for the exported video.
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      type="button"
                      title="Copy link"
                      onClick={() => copy(videoUrl, 'video')}
                      className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                    >
                      {copied === 'video' ? <Check className="h-3.5 w-3.5" /> : <Link2 className="h-3.5 w-3.5" />}
                    </button>
                    <button
                      type="button"
                      title="Open video page"
                      onClick={() => window.open(videoUrl, '_blank')}
                      className="p-1.5 rounded-md text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 transition-colors"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
